<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../dialog';
import { Button } from '../button';
import { Checkbox } from '../checkbox';
import { crossAppActions } from '../cross-app-actions';
import { alertModal } from '../modals/AlertModal.vue';
import { toast } from '../toast';
import type { AppID } from '../../cross-app-shared/types/db';
import type { AppAccessEffective } from '../../cross-app-shared/services/app-access';
import {
  type AppAccessNode,
  appAccessChain,
  appAccessEffective,
  appAccessFacilityCount,
  appAccessLevelLabels,
  appAccessOverridesBeneath,
  appAccessSettingOfCurrent,
  appAccessSettingOfStaged,
} from './app-access-tree';

const props = defineProps<{
  node: AppAccessNode;
  app: { appID: AppID; name: string };
}>();
const isOpen = defineModel<boolean>('isOpen', { required: true });
const emit = defineEmits<{ saved: [] }>();

const chain = computed(() => appAccessChain(props.node));
const chainKeys = computed(() => new Set(chain.value.map((n) => n.key)));
const settingOfCurrent = computed(() => appAccessSettingOfCurrent(props.app.appID));

// Every level of the chain is staged, initialized from what is saved today:
const staged = ref(new Map<string, boolean | null>());
const overridesBeneathClear = ref(new Set<string>());
const saving = ref(false);

function reset() {
  staged.value = new Map(chain.value.map((n) => [n.key, settingOfCurrent.value(n)]));
  overridesBeneathClear.value = new Set();
}
watch([() => props.node, () => props.app, isOpen], reset, { immediate: true });

const settingOfStaged = computed(() =>
  appAccessSettingOfStaged(settingOfCurrent.value, staged.value, overridesBeneathClear.value),
);

// Only levels the admin actually changed are sent, so saving doesn't revert edits made elsewhere since this loaded:
const changes = computed(() =>
  chain.value
    .map((node) => ({ node, isGrantedAccess: staged.value.get(node.key) ?? null }))
    .filter(({ node, isGrantedAccess }) => isGrantedAccess !== settingOfCurrent.value(node)),
);

const hasChanges = computed(() => overridesBeneathClear.value.size > 0 || changes.value.length > 0);

const levels = computed(() =>
  chain.value.map((node) => {
    const overridesBeneath = appAccessOverridesBeneath(node, settingOfCurrent.value, chainKeys.value);
    return {
      node,
      saved: node.settings[props.app.appID],
      overridesBeneath,
    };
  }),
);

const options: { value: boolean | null; label: string; icon: string; activeClass: string }[] = [
  { value: null, label: 'Inherit', icon: 'fa-light fa-arrow-down', activeClass: 'bg-slate-700 text-white' },
  { value: true, label: 'Grant', icon: 'fa-solid fa-check', activeClass: 'bg-emerald-600 text-white' },
  { value: false, label: 'Deny', icon: 'fa-solid fa-ban', activeClass: 'bg-red-600 text-white' },
];

function stagedSet(node: AppAccessNode, value: boolean | null) {
  const next = new Map(staged.value);
  next.set(node.key, value);
  staged.value = next;
}

function overridesBeneathClearToggle(node: AppAccessNode, value: boolean) {
  const next = new Set(overridesBeneathClear.value);
  if (value) {
    next.add(node.key);
  } else {
    next.delete(node.key);
  }
  overridesBeneathClear.value = next;
}

function effectiveDescribe(effective: AppAccessEffective): string {
  if (effective.decidedBy === null) {
    return 'No access (nothing set)';
  }

  const via = appAccessLevelLabels[effective.decidedBy].toLowerCase();
  return `${effective.isGrantedAccess ? 'Granted' : 'Denied'} by the ${via} setting`;
}

// Show the before → after summary for the node that was clicked, plus the top of the chain if that's different, since
// changes higher up affect many facilities at once.
const summaries = computed(() => {
  const nodes = [props.node];
  if (chain.value[0] !== props.node) {
    nodes.unshift(chain.value[0]);
  }

  return nodes.map((node) => ({
    node,
    before: appAccessEffective(node, settingOfCurrent.value),
    after: appAccessEffective(node, settingOfStaged.value),
    countsBefore: appAccessFacilityCount(node, settingOfCurrent.value),
    countsAfter: appAccessFacilityCount(node, settingOfStaged.value),
  }));
});

async function save() {
  saving.value = true;
  try {
    await crossAppActions.appAccess.update({
      appID: props.app.appID,
      changes: changes.value.map(({ node, isGrantedAccess }) => ({ target: node.target, isGrantedAccess })),
      overridesBeneathClear: chain.value.filter((n) => overridesBeneathClear.value.has(n.key)).map((n) => n.target),
      overridesBeneathKeep: chain.value.map((n) => n.target),
    });
    toast({ title: 'App access updated', description: `${props.app.name} access was saved.` });
    isOpen.value = false;
    emit('saved');
  } catch (e: any) {
    console.error(e);
    await alertModal({
      title: 'Unable to update app access',
      message: `An unexpected error occurred and we were unable to save. Error was: ${e.message}`,
    });
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <Dialog v-model:open="isOpen">
    <DialogContent class="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
      <DialogHeader>
        <DialogTitle>{{ app.name }} access for {{ node.name }}</DialogTitle>
        <DialogDescription>
          The most specific setting wins. A setting on a tenant or group applies to everything beneath it unless
          something lower down overrides it, so if the real fix belongs higher up, set it there.
        </DialogDescription>
      </DialogHeader>

      <div class="space-y-3">
        <div
          v-for="level of levels"
          :key="level.node.key"
          class="rounded-md border p-3"
          :class="level.node === node ? 'border-brand-blue bg-blue-50/50' : 'border-slate-200'"
        >
          <div class="flex flex-wrap items-center justify-between gap-3">
            <div class="min-w-0">
              <div class="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                {{ appAccessLevelLabels[level.node.target.level] }}
                <span v-if="level.node === node" class="text-brand-blue normal-case">· selected</span>
              </div>
              <div class="truncate font-medium">{{ level.node.name }}</div>
              <div class="text-muted-foreground text-xs">
                <template v-if="level.saved">
                  Currently {{ level.saved.isGrantedAccess ? 'granted' : 'denied' }} here by
                  {{ level.saved.createdByName }} on {{ new Date(level.saved.createdAt).toLocaleDateString() }}
                </template>
                <template v-else>Currently not set here</template>
              </div>
            </div>

            <div class="inline-flex overflow-hidden rounded-md border border-slate-200" role="radiogroup">
              <button
                v-for="option of options"
                :key="String(option.value)"
                type="button"
                role="radio"
                :aria-checked="staged.get(level.node.key) === option.value"
                class="flex items-center gap-1.5 px-3 py-1.5 text-sm transition-colors not-first:border-l not-first:border-slate-200"
                :class="
                  staged.get(level.node.key) === option.value ? option.activeClass : 'bg-white hover:bg-slate-100'
                "
                @click="stagedSet(level.node, option.value)"
              >
                <i :class="option.icon"></i>
                {{ option.label }}
              </button>
            </div>
          </div>

          <label
            v-if="level.overridesBeneath.length > 0"
            class="mt-3 flex cursor-pointer items-start gap-2 rounded bg-amber-50 p-2 text-sm"
          >
            <Checkbox
              class="mt-0.5"
              :modelValue="overridesBeneathClear.has(level.node.key)"
              @update:modelValue="overridesBeneathClearToggle(level.node, $event === true)"
            />
            <span>
              Also clear {{ level.overridesBeneath.length }} more-specific
              {{ level.overridesBeneath.length === 1 ? 'setting' : 'settings' }} beneath this
              {{ appAccessLevelLabels[level.node.target.level].toLowerCase() }}, so it applies everywhere:
              <span class="text-muted-foreground">
                {{
                  level.overridesBeneath
                    .slice(0, 5)
                    .map((n) => `${n.name} (${settingOfCurrent(n) ? 'granted' : 'denied'})`)
                    .join(', ')
                }}<template v-if="level.overridesBeneath.length > 5">
                  and {{ level.overridesBeneath.length - 5 }} more</template
                >
              </span>
            </span>
          </label>
        </div>
      </div>

      <div class="rounded-md bg-slate-50 p-3 text-sm">
        <div class="mb-2 font-medium">Result</div>
        <div v-for="summary of summaries" :key="summary.node.key" class="mb-2 last:mb-0">
          <div class="font-medium">{{ summary.node.name }}</div>
          <div>
            <span :class="summary.after.isGrantedAccess ? 'text-emerald-700' : 'text-red-700'">
              {{ effectiveDescribe(summary.after) }}
            </span>
            <span
              v-if="
                summary.before.isGrantedAccess !== summary.after.isGrantedAccess ||
                summary.before.decidedBy !== summary.after.decidedBy
              "
              class="text-muted-foreground"
            >
              (was: {{ effectiveDescribe(summary.before).toLowerCase() }})
            </span>
          </div>
          <div v-if="summary.node.target.level !== 'facility'" class="text-muted-foreground">
            {{ summary.countsAfter.granted }} of {{ summary.countsAfter.total }} facilities will have access
            <template v-if="summary.countsAfter.granted !== summary.countsBefore.granted">
              (currently {{ summary.countsBefore.granted }})
            </template>
          </div>
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" @click="isOpen = false">Cancel</Button>
        <Button :disabled="!hasChanges || saving" @click="save">
          <i v-if="saving" class="fa-light fa-spinner fa-spin"></i>
          Save
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
